import { Button, styled, useTheme } from "@monolith-forensics/monolith-ui";
import { useEffect, useRef, useState } from "react";

import {
  startTablet,
  stopTablet,
  clearTablet,
} from "../../utils/SigWebTablet.js";
import ReactSignatureCanvas from "react-signature-canvas";
import { Signature, SignatureFieldRef } from "./SignatureField";

const StyledOverlayContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.95);
  z-index: 1501;

  .header {
    display: flex;
    flex-direction: row;
    align-items: end;
    font-size: 1.2rem;
    font-weight: 500;
    margin-bottom: 10px;
    color: white;

    .subtle {
      font-size: 0.8rem;
      font-weight: 400;
      color: ${({ theme }) => theme.palette.text.secondary};
      margin-left: auto;
    }
  }

  .sig-container {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: all;
  }

  .sig-pad-container {
    width: 50vw;
    height: 25vh;
    max-height: 400px;
    max-width: 800px;
    min-height: 250px;
    min-width: 400px;
    border: 1px solid
      ${({ theme }) =>
        theme.name === "DARK"
          ? theme.palette.divider
          : "rgba(255,255,255,0.5)"};
    background-color: ${({ theme }) =>
      theme.name === "DARK"
        ? theme.palette.signature.backgroundColor
        : "rgba(255,255,255,0.75)"};
    border-radius: 5px;
  }

  .action-menu {
    display: flex;
    margin-top: 10px;
  }

  .clear-btn {
    cursor: pointer;
    color: red;
    display: flex;
    &:hover {
      text-decoration: underline;
    }
  }
`;

export type SignatureOverlayProps = {
  open: boolean;
  handleClose: () => void;
  onSave: (sig: Signature) => void;
  label?: string;
};

export const SignatureOverlay: React.FC<SignatureOverlayProps> = ({
  open,
  handleClose,
  onSave = () => {},
  label = "Sign Here",
}) => {
  const theme = useTheme();
  const sigPadRef = useRef<ReactSignatureCanvas | null>(null);
  const [isTopaz, setIsTopaz] = useState(false);

  const handleClear = () => {
    sigPadRef.current?.clear();
    if (isTopaz) {
      clearTablet();
    }
  };

  const handleSave = async () => {
    // Process Topaz signature pad data
    if (isTopaz) {
      stopTablet();
      setIsTopaz(false);

      const sig = sigPadRef.current?.getCanvas();

      const white = [255, 255, 255, 255];

      const sigImageData = sig
        ?.getContext("2d")
        ?.getImageData(0, 0, sig.width, sig.height);

      // Ensure that top right pixel is white
      // The Topaz sig pag returns a non-255 value for the top right pixel for some reason
      // This allows the trim canvas function to work properly by converting white space to transparent
      if (sigImageData) {
        sigImageData.data[3] = 255;

        // Convert white pixels to transparent
        for (let i = 0; i < sigImageData?.data.length; i += 4) {
          if (
            sigImageData.data[i] === white[0] &&
            sigImageData.data[i + 1] === white[1] &&
            sigImageData.data[i + 2] === white[2] &&
            sigImageData.data[i + 3] === white[3]
          ) {
            sigImageData.data[i + 3] = 0;
          }
        }

        sig?.getContext("2d")?.putImageData(sigImageData, 0, 0);
      }
    }

    const sigData = sigPadRef.current
      ?.getTrimmedCanvas()
      ?.toDataURL("image/png");

    onSave({
      signature: sigData || "",
      timestamp: new Date().toISOString(),
      userAgent: window.navigator.userAgent,
    });
    handleClose();
  };

  const handleCancel = () => {
    handleClose();

    if (isTopaz) {
      stopTablet();
      setIsTopaz(false);
    }
  };

  useEffect(() => {
    return () => {
      if (isTopaz) {
        stopTablet();
        setIsTopaz(false);
      }
    };
  }, []);

  return (
    <StyledOverlayContainer data-testid="signature-overlay">
      <div className="sig-container">
        {/* Reserved for ENG-1814: profile.spec asserts this header's "Sign
            Here" text once the blank label bug is fixed. */}
        <div className="header" data-testid="signature-overlay-header">
          <div>{label}</div>
          <div className="subtle">
            {isTopaz ? " (Signature Device Enabled)" : ""}
          </div>
        </div>
        <div className="sig-pad-container">
          <ReactSignatureCanvas
            ref={sigPadRef}
            clearOnResize={false}
            canvasProps={{
              onTouchMove: (event) => {
                event.stopPropagation();
              },
              style: {
                filter: theme.name === "DARK" ? "invert(1)" : "unset",
                width: "100%",
                height: "100%",
                touchAction: "none",
              },
            }}
            penColor={"black"}
          />
        </div>
        <div className="action-menu">
          <Button
            variant="outlined"
            size="xs"
            color="primary"
            disabled={isTopaz}
            onClick={() => {
              setIsTopaz(
                startTablet({
                  width: 100,
                  height: 100,
                })
              );
            }}
          >
            Use Sig Pad
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="xs"
            onClick={handleClear}
            style={{ marginLeft: 10 }}
          >
            Clear
          </Button>
          <div style={{ marginLeft: "auto", display: "flex" }}>
            <Button
              variant="subtle"
              size="xs"
              onClick={handleCancel}
              style={{
                color: "white",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="xs"
              style={{ marginLeft: 10 }}
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </StyledOverlayContainer>
  );
};
